import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/lib/auth-context";
import { ProtectedRoute, AttorneyGate } from "@/lib/ProtectedRoute";

import HomePage from "@/pages/HomePage";
import HowItWorksPage from "@/pages/HowItWorksPage";
import AttorneysPage from "@/pages/AttorneysPage";
import AboutPage from "@/pages/AboutPage";
import IntakePage from "@/pages/IntakePage";
import SmsFunnelPage from "@/pages/SmsFunnelPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import PendingPage from "@/pages/PendingPage";
import BillingPage from "@/pages/BillingPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import AttorneyDashboard from "@/pages/AttorneyDashboard";
import AttorneyCaseDetail from "@/pages/AttorneyCaseDetail";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminUsers from "@/pages/AdminUsers";
import AdminCases from "@/pages/AdminCases";
import AdminAudit from "@/pages/AdminAudit";
import AdminBilling from "@/pages/AdminBilling";
import TerritoryBuilder from "@/pages/TerritoryBuilder";
import AuthCallback from "@/pages/AuthCallback";
import MarketingLayout from "@/components/MarketingLayout";
import AttorneyLayout from "@/components/AttorneyLayout";
import AdminLayout from "@/components/AdminLayout";

function AppRouter() {
  const location = useLocation();
  // Synchronous OAuth callback detection — prevents race conditions with AuthProvider
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/how-it-works" element={<HowItWorksPage />} />
        <Route path="/attorneys" element={<AttorneysPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
      </Route>

      <Route path="/intake" element={<IntakePage />} />
      {/* Mobile-first SMS funnel for inbound text-message campaigns. Lives
          OUTSIDE MarketingLayout so it has no nav/footer (one action per
          screen). Two aliases for campaign flexibility. */}
      <Route path="/sms" element={<SmsFunnelPage />} />
      <Route path="/get-help" element={<SmsFunnelPage />} />

      <Route
        path="/pending"
        element={
          <ProtectedRoute roles={["attorney", "law_firm"]}>
            <PendingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute roles={["attorney", "law_firm"]}>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <ProtectedRoute roles={["attorney", "law_firm"]}>
            <BillingSuccessPage />
          </ProtectedRoute>
        }
      />

      <Route
        element={
          <AttorneyGate>
            <AttorneyLayout />
          </AttorneyGate>
        }
      >
        <Route path="/attorney/dashboard" element={<AttorneyDashboard />} />
        <Route path="/attorney/cases/:caseId" element={<AttorneyCaseDetail />} />
        <Route path="/attorney/territory" element={<TerritoryBuilder />} />
      </Route>

      <Route
        element={
          <ProtectedRoute roles={["admin"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/cases" element={<AdminCases />} />
        <Route path="/admin/audit" element={<AdminAudit />} />
        <Route path="/admin/billing" element={<AdminBilling />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
